import { isBefore, isSameDay, parseISO } from 'date-fns'
import { Calendar, Clock, FileText, User } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Separator } from '@/components/ui/separator'
import { formatDate } from '@/utils/DateHelpers'

import type { EmployeeLeaveData } from '../pages/LeaveCalendarPage'

// Helper function to get initials from a name string
function getInitialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

// Color palette for different leave types
const leaveTypeColors: string[] = [
  '#3b82f6', // Blue
  '#22c55e', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#6366f1', // Indigo
  '#06b6d4', // Cyan
]

// Function to get consistent color for a leave type
function getLeaveTypeColor(leaveTypeName: string | undefined, leaveTypeId: number | undefined): string {
  if (!leaveTypeName && !leaveTypeId)
    return leaveTypeColors[0]
  // Use leave type ID for consistent coloring, fallback to name hash
  const index = leaveTypeId
    ? leaveTypeId % leaveTypeColors.length
    : (leaveTypeName?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0) % leaveTypeColors.length
  return leaveTypeColors[index]
}

const statusBadgeVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  APPROVED: 'default',
  PENDING: 'secondary',
  SUBMITTED: 'secondary',
  REJECTED: 'destructive',
  CANCELLED: 'outline',
  DRAFT: 'outline',
}

interface EmployeeRowProps {
  employee: EmployeeLeaveData
  calendarDays: Date[]
  dayWidth: number
  today: Date
  onLeaveClick: (leaveId: number) => void
}

export function EmployeeRow({ employee, calendarDays, dayWidth, today, onLeaveClick }: EmployeeRowProps) {
  const todayIndex = calendarDays.findIndex(day => isSameDay(day, today))

  return (
    <div className="group flex rounded-lg border bg-card hover:bg-accent/50">
      {/* Employee Info */}
      <div className="sticky left-0 z-20 flex w-56 shrink-0 items-center gap-3 border-r bg-card p-3 group-hover:bg-accent/100">
        <Avatar className="size-8">
          <AvatarImage
            src={employee.userAvatar?.trim() || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee.userName)}&background=E2E8F0&color=0F172A&size=128`}
            alt={employee.userName}
          />
          <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
            {getInitialsFromName(employee.userName)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate text-sm font-medium">{employee.userName}</span>
      </div>

      {/* Calendar Days Grid */}
      <div className="relative flex flex-1">
        {calendarDays.map((day) => {
          const isToday = isSameDay(day, today)
          const isPastDay = isBefore(day, today)
          const dayClassName = isToday
            ? 'bg-primary/40'
            : isPastDay
              ? 'bg-gray-300'
              : ''

          return (
            <div
              key={day.toISOString()}
              className={`border-l first:border-l-0 ${dayClassName}`}
              style={{ minWidth: `${dayWidth}px` }}
            />
          )
        })}

        {/* Leave Bars */}
        <div className="absolute inset-0 px-0 py-2">
          {employee.leaves.map((leave) => {
            if (!leave.startDate || !leave.endDate || !leave.id)
              return null

            try {
              const leaveStart = parseISO(leave.startDate)
              const leaveEnd = parseISO(leave.endDate)

              // Find start and end positions in calendar
              const startIndex = calendarDays.findIndex(day =>
                isSameDay(day, leaveStart),
              )
              const endIndex = calendarDays.findIndex(day => isSameDay(day, leaveEnd))

              // Skip if leave is outside visible range
              if (startIndex === -1 && endIndex === -1)
                return null

              const adjustedStartIndex = Math.max(0, startIndex)
              const adjustedEndIndex
                = endIndex === -1 ? calendarDays.length - 1 : endIndex

              const span = adjustedEndIndex - adjustedStartIndex + 1
              const left = adjustedStartIndex * dayWidth
              const width = span * dayWidth
              const bgColor = getLeaveTypeColor(leave.leaveTypeName, leave.leaveTypeId)
              const isLeaveInPast = isBefore(leaveEnd, today)

              // Add opacity for non-approved leaves
              const baseOpacity = leave.status === 'APPROVED' ? 1 : 0.75
              const opacity = isLeaveInPast ? Math.min(baseOpacity, 0.55) : baseOpacity
              const pastFillColor = '#9ca3af'
              let barColor = bgColor
              let backgroundImage: string | undefined

              if (todayIndex >= 0) {
                const pastSpan = Math.max(0, Math.min(span, todayIndex - adjustedStartIndex))
                if (pastSpan >= span) {
                  barColor = pastFillColor
                }
                else if (pastSpan > 0) {
                  const pastPercent = (pastSpan / span) * 100
                  backgroundImage = `linear-gradient(to right, ${pastFillColor} 0%, ${pastFillColor} ${pastPercent}%, ${bgColor} ${pastPercent}%, ${bgColor} 100%)`
                }
              }
              else if (isLeaveInPast) {
                barColor = pastFillColor
              }

              return (
                <HoverCard key={leave.id} openDelay={200}>
                  <HoverCardTrigger asChild>
                    <div
                      onClick={() => onLeaveClick(leave.id!)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onLeaveClick(leave.id!)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className="group absolute flex h-3 items-center gap-1 rounded-full px-3 text-left shadow-sm transition-shadow hover:z-10 hover:shadow-md"
                      style={{
                        left: `${left}px`,
                        width: `${width}px`,
                        backgroundColor: barColor,
                        backgroundImage,
                        opacity,
                        top: '8px',
                      }}
                    />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80" side="top">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{leave.leaveTypeName}</h4>
                          <p className="text-sm text-muted-foreground">{employee.userName}</p>
                        </div>
                        <Badge variant={statusBadgeVariants[leave.status ?? 'PENDING']}>
                          {leave.status?.replaceAll('_', ' ')}
                        </Badge>
                      </div>

                      <Separator />

                      {/* Details */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="size-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Start:</span>
                          <span className="font-medium">{formatDate(leave.startDate)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="size-4 text-muted-foreground" />
                          <span className="text-muted-foreground">End:</span>
                          <span className="font-medium">{formatDate(leave.endDate)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="size-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="font-medium">
                            {leave.requestedDays}
                            {' '}
                            days
                          </span>
                        </div>
                        {leave.departmentName && (
                          <div className="flex items-center gap-2">
                            <User className="size-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Department:</span>
                            <span className="font-medium">{leave.departmentName}</span>
                          </div>
                        )}
                        {leave.currentApproverName && (
                          <div className="flex items-center gap-2">
                            <FileText className="size-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Current Approver:</span>
                            <span className="font-medium">{leave.currentApproverName}</span>
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* Footer */}
                      <p className="text-xs text-muted-foreground">
                        Click for full details and approval timeline
                      </p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              )
            }
            catch (error) {
              console.error('Error rendering leave bar:', error)
              return null
            }
          })}
        </div>
      </div>
    </div>
  )
}
